import {
    Button,
    Checkbox,
    Dialog,
    FormControl,
    FormControlLabel,
    FormHelperText,
    MenuItem,
    Select,
} from '@material-ui/core';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import en from '../../../../lang/en-us';
import TextField from '../../../../lib/TextField';
import { RootStateType } from '../../../../redux-store/reducers';
import { AccountTypes, IsAllow } from '../../../../redux-store/types/portfolio';
import BankDetails from './BankDetails';
import { ExtraValidationsType, InitStateType, RegisterOtmReqType, RTA_CODES } from './types';
import UserDetails from './UserDetails';
import {
    eMandateSchema,
    MIN_EMANDATE_MONTHS,
    otmFrequency,
    rtaCodes,
    VALIDATED_IFSC,
} from './utils';
import {
    getOtmAllowedBankDetails,
    registerNewotm,
} from '../../../../redux-store/actions/newPurchase';
import {
    OtmAllowedBankAPIResponse,
    OtmBankAllowedData,
} from '../../../../redux-store/types/newPurchase';
import { getDateString, ifscRegex, isMobileDevice, MAX_DATE, OTM_MAX_DATE } from '../../../../utils/constants';
import { CustomRadio } from '../../../../lib/CustomRadio';
import { SubmitButtonWithLoader } from '../../../../lib/Button';
import { getInvestorLatestDetails } from '../../../../redux-store/actions/commercialTransactions';
import { monthDiff } from '../../../../hooks/useSip';
import { addDays } from 'date-fns';
import { useHistory } from 'react-router';
import { CommercialRootPath } from '../routeConfig';

const { dbCommercial, common } = en;
let tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);

export default function eMandate(props: any) {
    const [show, setShow] = useState<boolean>(false);
    const [banks, setBanks] = useState<OtmBankAllowedData[]>([]);
    const [tcOpen, setTcOpen] = useState<boolean>(false);
    const [extraValidations, setExtraValidations] = useState<ExtraValidationsType | null>(null);
    const [latestDataCalled, setLatestDataCalled] = useState<any>(null);
    const [initiated, setInitiated] = useState<boolean>(false);
    const { email, pan, displayName, purchaseDetails, displayMobile } = useSelector(
        ({ authReducer, newPurchase }: RootStateType) => ({
            ...authReducer,
            ...newPurchase,
        }),
    );
    const history = useHistory();
    function onMobileRedirect(url: string) {
        // const onExit = () => {
        //     history.push(`${CommercialRootPath}/otm-status/${reqId}`);
        //  }
        const onBeforeLoad = (params: any, callback: any) => {
            if (params.url.includes(`otm-status`)) {
                ref.close();
                const _urlArgs = params.url.split('/');
                const reqId = _urlArgs.pop();
                if (!!reqId) {
                    const url = `${CommercialRootPath}/otm-status/${reqId}`;
                    history.push(url);
                    return;
                }
                history.push('/');
            } else {
                callback(params.url);
            }
        };
        const onLoadStart = (event: any) => {
            if (event.url.includes(`otm-status`)) {
                ref.close();
                const _urlArgs = event.url.split('/');
                const reqId = _urlArgs.pop();
                if (!!reqId) {
                    const url = `${CommercialRootPath}/otm-status/${reqId}`;
                    history.replace(url);
                    return;
                }
                history.replace('/');
            }
        };
        const _window: any = window;
        const options =
            _window?.device?.platform === 'Android'
                ? 'hideurlbar=yes,hidenavigationbuttons=yes,beforeload=get'
                : 'location=no,hidenavigationbuttons=yes,beforeload=get';
        const ref = _window.cordova.InAppBrowser.open(url, '_blank', options);
        // ref.addEventListener('exit', onExit)
        ref.addEventListener('beforeload', onBeforeLoad);
        ref.addEventListener('loadstart', onLoadStart);
    }
    const formik = useFormik<InitStateType>({
        initialValues: {
            name: displayName,
            email,
            pan,
            mobile: displayMobile,
            accountNo: '',
            accountType: AccountTypes.SAVINGS,
            accountHolder: '',
            branch: '',
            city: '',
            pincode: '',
            micr: '',
            ifsc: '',
            neftIfsc: '',
            confirmAccountNumber: '',
            bankDefault: IsAllow.YES,
            frequency: otmFrequency.value,
            amount: '',
            toDate: tomorrow,
            fromDate: addDays(new Date(), 1),
            allowedBanks: [],
            bankName: { bankName: '', ifscPrefixCode: '' },
            untilCancelled: IsAllow.No,
            rtaCode: null,
            declaration: false,
        },
        onSubmit: async (values: InitStateType) => {
            if (
                values.bankName.ifscPrefixCode.toUpperCase() !==
                values.ifsc.slice(0, 4).toUpperCase()
            ) {
                formik.setFieldError(
                    'bankName',
                    dbCommercial.exploreMfs.eMandate.form.required.ifscAndBank,
                );
                formik.setFieldError(
                    'ifsc',
                    dbCommercial.exploreMfs.eMandate.form.required.ifscAndBank,
                );
                return;
            }
            if (!formik.values.declaration) return;
            const payload: RegisterOtmReqType = {
                mandateDetails: {
                    amount: values.amount,
                    category: 'M001',
                    debitType: 'M',
                    endDate: getDateString(
                        formik.values.untilCancelled === IsAllow.YES ? OTM_MAX_DATE(values.fromDate) : values.toDate,
                    ),
                    startDate: getDateString(values.fromDate),
                    frequency: values.frequency,
                    otmType: 'EOTM',
                    untilCancelled: values.untilCancelled,
                },
                amc: '',
                bank: {
                    accountNo: values.accountNo,
                    name: values.bankName.bankName,
                    ifsc: values.ifsc,
                    branch: values.branch,
                    city: values.city,
                    micr: values.micr,
                    neftIfscCode: values.neftIfsc,
                    holderName: values.accountHolder?.trim(),
                    accountType: values.accountType,
                },
                otmEmail: values.email,
                otmMobile: values.mobile,
                source: 'MFC',
                rtaCode: formik.values.rtaCode as RTA_CODES,
                investorName: values.name,
            };
            try {
                if (
                    values.untilCancelled === IsAllow.No &&
                    monthDiff(new Date(values.fromDate), new Date(values.toDate)) <
                    MIN_EMANDATE_MONTHS
                ) {
                    throw new Error(dbCommercial.exploreMfs.eMandate.form.lengths.eMandateDuration);
                }
                const response: any = await dispatch(registerNewotm({ data: [payload] }));
                formik.setSubmitting(false);
                const { redirectUrl } = response.body;
                if (isMobileDevice()) {
                    onMobileRedirect(redirectUrl);
                } else {
                    window.open(redirectUrl, '_self');
                }
            } catch (err: any) {
                alert(err.message);
            }
        },
        validationSchema: eMandateSchema,
        validate: (values: InitStateType) => {
            // Making not validated while edited
            // Make only not validated only it success and regex matched
            if (!values.ifsc?.match(ifscRegex) && formik.status === VALIDATED_IFSC) {
                formik.setStatus(null);
                return;
            }
        },
    });
    const dispatch = useDispatch();

    useEffect(() => {
        async function getAllowedBanks() {
            try {
                const response: any = await dispatch(
                    getOtmAllowedBankDetails({ rtaCode: RTA_CODES.KFIN }),
                );
                const { data = [] }: OtmAllowedBankAPIResponse = response.body || {};
                data.sort((a, b) => a.bankName.localeCompare(b.bankName));
                setBanks(data);
            } catch (err: any) {
                alert(err.message);
            }
        }
        async function getInvestorData() {
            try {
                await dispatch(getInvestorLatestDetails());
            } catch (err: any) {
                console.log(err.message);
            } finally {
                setLatestDataCalled(true);
            }
        }
        getAllowedBanks();
        getInvestorData();
    }, []);

    const memorizedBanks = useMemo(() => {
        if (!formik.values.rtaCode) return formik.values.allowedBanks;
        return banks.filter((bank) => bank.rtaCode === formik.values.rtaCode);
    }, [formik.values.rtaCode, banks]);

    useEffect(() => {
        const [investorBank] = purchaseDetails?.bank || [];
        const { investorName, email: _email, mobile: _mobile } = purchaseDetails;
        if (!initiated && latestDataCalled && formik.values.rtaCode && !!banks.length) {
            const existingBank = memorizedBanks.find(
                (_bank) =>
                    investorBank.ifsc?.slice(0, 4)?.toUpperCase() ===
                    _bank.ifscPrefixCode?.toUpperCase(),
            );
            if (!existingBank) {
                setExtraValidations({
                    bankName: {
                        bankName: `${investorBank.name} is not supported by ${rtaCodes.find((rta) => rta.value === formik.values.rtaCode)?.label
                            }`,
                    },
                });
            }
            const { accountNo, branch, city, pincode, micr, ifsc, neftIfsc } = investorBank;
            formik.setValues({
                ...formik.values,
                accountNo,
                // accountType,
                branch,
                city,
                pincode,
                micr,
                ifsc: ifsc || neftIfsc,
                neftIfsc,
                name: investorName || displayName,
                email: !!formik.values.email ? formik.values.email : _email,
                mobile: !!formik.values.mobile ? formik.values.mobile : _mobile,
                bankName: existingBank ?? formik.values.bankName,
                allowedBanks: memorizedBanks,
            });
            setInitiated(true);
        }
    }, [memorizedBanks, purchaseDetails, latestDataCalled, banks]);

    useEffect(() => {
        formik.setFieldValue('allowedBanks', memorizedBanks);
        if (initiated && formik.values.bankName.ifscPrefixCode) {
            const existingBank = memorizedBanks.find(
                (_bank) => _bank.ifscPrefixCode === formik.values.bankName.ifscPrefixCode,
            );
            if (!existingBank) {
                formik.setFieldValue('bankName', formik.initialValues.bankName);
                formik.setFieldTouched('bankName', false);
                setExtraValidations({
                    bankName: {
                        bankName: `${formik.values.bankName.bankName} is not supported by ${rtaCodes.find((rta) => rta.value === formik.values.rtaCode)?.label
                            }`,
                    },
                });
            }
        } else {
            setExtraValidations({
                bankName: null,
            });
        }
    }, [memorizedBanks]);

    return (
        <div className="curved-bg mf-investment mf-payment e-mandate">
            <h2 className="heading">
                <a onClick={() => props.history.goBack()}>
                    <img src="/images/left-arrow-black.svg" alt="icon" />
                </a>
                {dbCommercial.exploreMfs.eMandate.registerMandate}
            </h2>
            <CustomRadio
                extraClass="custom-radio"
                title={''}
                name="select-rta"
                list={rtaCodes}
                selectedValue={formik.values.rtaCode}
                handleRadioChange={(value: RTA_CODES) => formik.setFieldValue('rtaCode', value)}
            />
            {!!formik.values.rtaCode && (
                <div className="curved-bg-content">
                    <div className="mf-investment-left">
                        <div className="investment-details">
                            <div className="new-otm-content">
                                <UserDetails formik={formik} latestDataCalled={latestDataCalled} />
                                <BankDetails
                                    formik={formik}
                                    latestDataCalled={latestDataCalled}
                                    extraErrors={extraValidations}
                                    setExtraErrors={setExtraValidations}
                                />
                            </div>
                            <div className="mf-investment-bottom">
                                <FormControlLabel
                                    className="inline-checkbox"
                                    control={
                                        <Checkbox
                                            name="terms-conditions"
                                            onChange={(e) => {
                                                formik.setFieldValue(
                                                    'declaration',
                                                    e.target.checked,
                                                );
                                                formik.setFieldTouched('declaration', true);
                                            }}
                                            value={formik.values.declaration}
                                        />
                                    }
                                    label={
                                        <>
                                            {dbCommercial.exploreMfs.mfInvestment.disclaimers}
                                            <a
                                                onClick={() => setTcOpen(!tcOpen)}
                                                style={{ color: '#836fe0' }}
                                            >
                                                {dbCommercial.exploreMfs.mfInvestment.termsLink}
                                            </a>
                                            {dbCommercial.exploreMfs.mfInvestment.of}
                                            <span>
                                                {dbCommercial.exploreMfs.mfInvestment.mfCentral}
                                            </span>
                                        </>
                                    }
                                />
                                {formik.touched.declaration && !formik.values.declaration && (
                                    <FormHelperText
                                        style={{
                                            color: 'red',
                                            fontSize: 14,
                                            textAlign: 'left',
                                        }}
                                    >
                                        {dbCommercial.errors.termsAndCondition}
                                    </FormHelperText>
                                )}
                                <div className="buttons">
                                    <SubmitButtonWithLoader
                                        className="customBtn "
                                        onClick={formik.handleSubmit as any}
                                        label={dbCommercial.exploreMfs.eMandate.submit}
                                        loading={formik.isSubmitting}
                                        labelWhileLoading={'Sending'}
                                        disabled={formik.isSubmitting}
                                    />
                                </div>
                            </div>
                            <div className="mf-investment-npci-logo">
                                <img src="/images/npci.png" alt="icon" />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Dialog fullWidth maxWidth={'lg'} open={tcOpen} aria-labelledby="terms-modal">
                <div className="terms-modal">
                    <div className="heading">
                        <h2>{common.termsConditions}</h2>
                        <a className="close">
                            <img
                                onClick={() => setTcOpen(!tcOpen)}
                                src="/images/close-white.svg"
                                alt="icon"
                            />
                        </a>
                    </div>
                    <div className="content-outer">
                        <div className="content-inner">
                            {formik.values.rtaCode === RTA_CODES.CAMS
                                ? Object.values(dbCommercial.termsModal.camsEMandate).map(
                                    (item) => <p> {item} </p>,
                                )
                                : Object.values(dbCommercial.termsModal.kfinEMandate).map(
                                    (item) => <p> {item} </p>,
                                )}
                        </div>
                    </div>
                </div>
            </Dialog>
            <Dialog
                maxWidth={'sm'}
                open={show}
                aria-labelledby="newbank-mandate-modal"
                onClose={() => setShow(false)}
            >
                <div className="note-modals newbank-mandate-modal">
                    <h3>{dbCommercial.exploreMfs.mfPayment.modal.heading}</h3>
                    <a onClick={() => setShow(!show)} className="close">
                        <img src="/images/close-black.svg" alt="icon" />
                    </a>
                    <div className="note-modals-content">
                        <div className="mandate-amount">
                            <TextField
                                label={dbCommercial.exploreMfs.mfPayment.modal.mandateAmount}
                                id="enter-arn"
                                variant="outlined"
                            />
                            <p>
                                <img src="/images/exclaim.svg" alt="icon" />
                                Valid From (Today)
                                <span>30-12-2021</span>
                            </p>
                        </div>
                        <FormControl variant="filled" className="bank-account-select">
                            <Select labelId="reg-mode-label" id="reg-mode-sortby" value="0">
                                <MenuItem value={0}>
                                    {dbCommercial.exploreMfs.mfPayment.modal.bankAccount}
                                </MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label={dbCommercial.exploreMfs.mfPayment.modal.ifsc}
                            id="enter-arn"
                            variant="outlined"
                        />
                        <div className="note-modals-btns">
                            <Button className="customBtn" onClick={() => setShow(!show)}>
                                {dbCommercial.exploreMfs.mfPayment.modal.submit}
                            </Button>
                        </div>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
